import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Button,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  Icon,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { AddCryptoAddressResponse } from 'types/whitelisting'
import DeleteAddressModal from './DeleteModal/DeleteAddressModal'

interface CryptoAddressTableProps {
  cryptoAddresses: AddCryptoAddressResponse[]
  showDeleteModal: boolean
  setShowDeleteModal: Dispatch<SetStateAction<boolean>>
  handleClose: () => void
  onDeleteCallback: () => void
  isSearching: boolean
  canEdit: boolean
}
const CryptoAddressTable = ({
  cryptoAddresses,
  showDeleteModal,
  setShowDeleteModal,
  handleClose,
  onDeleteCallback,
  isSearching,
  canEdit,
}: CryptoAddressTableProps) => {
  const { t } = useTranslation()

  const [selectedCryptoAddress, setSelectedCryptoAddress] = useState()

  const setCryptoId = useCallback(
    address => {
      setSelectedCryptoAddress(address)
    },
    [selectedCryptoAddress]
  )

  const columns: DataTableHeaderProps[] = [
    {
      key: 'cryptocurrency',
      variant: DataTableCellVariants.TEXT,
      label: t('manageCryptoAddresses.columns.cryptocurrency'),
    },
    {
      key: 'address',
      variant: DataTableCellVariants.TEXT,
      label: t('manageCryptoAddresses.columns.address'),
    },
    {
      key: 'reason',
      variant: DataTableCellVariants.TEXT,
      label: t('manageCryptoAddresses.columns.reason'),
    },
    {
      key: 'added',
      variant: DataTableCellVariants.TEXT,
      label: t('manageCryptoAddresses.columns.added'),
    },
    { key: 'actions', variant: DataTableCellVariants.ACTION, label: '' },
  ]

  const tableData: DataTableRowData[] = useMemo(() => {
    return cryptoAddresses?.map(cryptoAddress => {
      const hasCreator = !!cryptoAddress?.creator
      const hasDateCreated = !!cryptoAddress?.dateCreated

      const hasCreatorOnly = hasCreator && !hasDateCreated
      const dateDateCreatedOnly = hasDateCreated && !hasCreator
      const hasCreatorAndDate = hasCreator && hasDateCreated

      return {
        id: cryptoAddress.uuid,
        testid: cryptoAddress.uuid,
        cells: [
          {
            key: 'cryptocurrency',
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar type="currency" name={cryptoAddress?.currency?.code} />,
            Text: `${cryptoAddress?.currency?.code} ${
              !!cryptoAddress?.protocol ? `(${cryptoAddress?.protocol})` : ''
            }`,
            BottomText: cryptoAddress?.currency?.name,
          },
          {
            key: 'address',
            variant: DataTableCellVariants.TEXT,
            Text: cryptoAddress?.address,
            BottomText: cryptoAddress?.name,
          },
          {
            key: 'reason',
            variant: DataTableCellVariants.TEXT,
            Text: cryptoAddress?.reason,
          },
          {
            key: 'added',
            variant: DataTableCellVariants.TEXT,
            Text: dayjs(cryptoAddress?.dateCreated).format('DD MMM YYYY'),
            BottomText: cryptoAddress?.creator,
            ...(hasCreatorAndDate && {
              Text: cryptoAddress?.creator,
              BottomText: dayjs(cryptoAddress?.dateCreated).format('DD MMM YYYY'),
            }),
            ...((hasCreatorOnly || dateDateCreatedOnly) && {
              Text:
                cryptoAddress?.creator ?? dayjs(cryptoAddress?.dateCreated).format('DD MMM YYYY'),
            }),
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: canEdit
              ? [
                  <Button
                    key={`delete-address-${cryptoAddress?.uuid}`}
                    iconElement={<Icon name="Trash" />}
                    testId={`delete-address-${cryptoAddress?.uuid}`}
                    variant="ghost"
                    onClick={() => {
                      setCryptoId(cryptoAddress)
                      setShowDeleteModal(true)
                    }}
                  />,
                ]
              : [],
          },
        ],
      }
    })
  }, [cryptoAddresses])

  return (
    <>
      {showDeleteModal && (
        <DeleteAddressModal
          handleOnModalClose={handleClose}
          selectedCryptoAddress={selectedCryptoAddress}
          onDeleteCallback={onDeleteCallback}
        />
      )}
      {isSearching ? (
        <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
      ) : (
        <DataTable headers={columns} data={tableData} />
      )}
    </>
  )
}

export default CryptoAddressTable
